<div class="verification-details-container">
  <div class="details-header" (click)="onToggleExpand($event)">
    <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="16px" style="flex: 1;">
      <span class="mdi" [ngClass]="isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"></span>
      <div class="header-title-group">
        <span class="header-title">Verification Details</span>
      </div>
    </div>
    <div fxLayout="row" fxLayoutGap="10px">
      @if (showSyncFromFrankieOneButton) {
      <span (click)="$event.stopPropagation()"
        [matTooltip]="syncTooltip">
        <button mat-stroked-button (click)="onSyncFromFrankieOne($event)" [disabled]="syncDisabled || syncInProgress">
          @if (syncInProgress) {
          <span class="mdi mdi-loading mdi-spin mr-2"></span>
          } @else {
          <span class="mdi mdi-refresh mr-2"></span>
          }
          Refresh KYC
        </button>
      </span>
      }
      @if (showUpdateKycButton) {
      <button mat-stroked-button (click)="onUpdateKyc($event)">
        <span class="mdi mdi-account-check-outline mr-2"></span>
        Update KYC
      </button>
      }
      @if (showSendSmsButton) {
      <span (click)="$event.stopPropagation()"
        [matTooltip]="sendSmsTooltip"
        [matTooltipDisabled]="!sendSmsTooltip">
        <button mat-stroked-button (click)="onSendSms($event)" [disabled]="sendSmsDisabled">
          @if (sendSmsInProgress) {
          <span class="mdi mdi-loading mdi-spin mr-2"></span>
          } @else {
          <span class="mdi mdi-cellphone-message mr-2"></span>
          }
          KYC SMS to Individual
        </button>
      </span>
      }
    </div>
  </div>

  <div class="status-banner-container" fxLayout="row" fxLayoutAlign="center center">
    <message-box [type]="statusMessage.type" class="status-message-box">
      <span class="status-text">{{ statusMessage.message }}</span>
    </message-box>
  </div>

  @if (isExpanded) {
  <div class="details-content">
    <!-- Primary Metadata Grid -->
    <div class="metadata-grid">
      <div class="metadata-item">
        <span class="label">Verification Status</span>
        <span class="value">
          <tag-box [state]="verificationStatusDisplay(status)"></tag-box>
        </span>
      </div>
      @if (lastUpdate) {
      <div class="metadata-item">
        <span class="label">Last Update</span>
        <span class="value">{{lastUpdate | date: 'MMM d, y, h:mm:ss a'}}</span>
      </div>
      }
      <div class="metadata-item">
        <span class="label">Request Status</span>
        <span class="value">{{requestStatusDisplay(digitalIdResponse?.verification_status)}}</span>
      </div>
      @if (digitalIdResponse?.overallRiskLevel) {
      <div class="metadata-item">
        <span class="label">Overall Risk Level</span>
        <span class="value risk-level" [ngClass]="digitalIdResponse?.overallRiskLevel?.toLowerCase()">
          {{digitalIdResponse?.overallRiskLevel}}
        </span>
      </div>
      }
      @if (digitalIdResponse?.transaction_id) {
      <div class="metadata-item">
        <span class="label">Transaction ID</span>
        <span class="value">{{digitalIdResponse?.transaction_id}}</span>
      </div>
      }
      @if (digitalIdResponse?.verificationMethod) {
      <div class="metadata-item">
        <span class="label">Verification Method</span>
        <span class="value">{{digitalIdResponse?.verificationMethod}}</span>
      </div>
      }
      @if (digitalIdResponse?.verifiedByName) {
      <div class="metadata-item">
        <span class="label">Verified By</span>
        <span class="value">{{digitalIdResponse?.verifiedByName}}</span>
      </div>
      }
      @if (frankieOneEntityId) {
      <div class="metadata-item">
        <span class="label">Entity ID</span>
        <span class="value">{{frankieOneEntityId}}</span>
      </div>
      }
    </div>

    <!-- Suggestions section -->
    @if (verificationError?.suggestions?.length) {
    <div class="info-section suggestions">
      <div class="section-header">
        <span class="mdi mdi-lightbulb-outline"></span>
        Next Steps
      </div>
      <ul class="simple-list">
        @for (suggestion of verificationError!.suggestions; track suggestion) {
        <li>{{ suggestion }}</li>
        }
      </ul>
    </div>
    }

    <!-- Issues section -->
    @if (showIssues && issues.length > 0) {
    <div class="info-section issues">
      <div class="section-header">
        <span class="mdi mdi-alert-circle-outline"></span>
        Issues Found ({{ issues.length }})
      </div>
      <div class="issues-list">
        @for (issue of issues; track issue.issueId) {
        <div class="issue-row">
          <span class="severity-dot" [ngClass]="getSeverityClass(issue.severity)"></span>
          <span class="severity-text">{{ issue.severity }}</span>
          <span class="category">{{ issue.category }}</span>
          <span class="type">{{ formatIssueType(issue.issue) }}</span>
          @if (issue.mappedDescription) {
          <span class="description">- {{ issue.mappedDescription }}</span>
          }
        </div>
        }
      </div>
    </div>
    }

    <!-- Watchlist Information -->
    @if (digitalIdResponse?.watchlist) {
    <div class="watchlist-info">
      <div class="section-header">Watch List Result</div>
      <div fxLayout="row" fxLayoutGap="20px" class="mb-2">
        <span>Performed: <strong>{{capitalize(digitalIdResponse.watchlist.check_performed)}}</strong></span>
        <span>Found: <strong [class.error]="digitalIdResponse.watchlist.found">{{capitalize(digitalIdResponse.watchlist.found)}}</strong></span>
      </div>
      @if (digitalIdResponse.watchlist.found_sources?.length) {
      <ul class="watchlist-sources">
        @for (source of digitalIdResponse.watchlist.found_sources; track source) {
        <li>{{source.category}} - {{source.name}}</li>
        }
      </ul>
      }
    </div>
    }

    <!-- Verification History Timeline -->
    @if (hasHistory && showIssues) {
    <div class="verification-history-section">
      <div class="section-header">
        <span class="mdi mdi-history"></span>
        Verification History
      </div>
      <div class="history-timeline">
        @for (event of sortedHistory; track event.id) {
        <div class="history-item" [ngClass]="'event-' + event.eventType.toLowerCase()">
          <div class="history-item-header">
            <span class="mdi {{ getEventIcon(event.eventType) }} mr-1"></span>
            <span class="event-type">{{ getEventTypeDisplay(event.eventType) }}</span>
            <span class="event-date">{{ event.createdAt | date:'dd/MM/yyyy h:mm a' }}</span>
          </div>
          <div class="history-item-body">
            <div class="event-status">
              Status: {{ verificationStatusDisplay(event.status) }}
            </div>
            @if (event.note) {
            <div class="event-note">
              <strong>Note:</strong> {{ event.note }}
            </div>
            }
            @if (event.error) {
            <div class="event-error">
              <span class="mdi mdi-alert-circle-outline"></span>
              {{ event.error }}
            </div>
            }
            @if (event.createdBy) {
            <div class="event-user">By: {{ event.createdBy }}</div>
            }
          </div>
        </div>
        }
      </div>
    </div>
    }
  </div>
  }
</div>
